import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fieldWorks as api, clients as clientsApi,
  type FieldWork, type FieldWorkFilter, type FieldWorkMeta, type FieldWorkSummary,
  type FieldWorkStatusCode, type FieldWorkKindCode,
} from '@/api/endpoints';
import type { Client } from '@/types';
import { hasPermission } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Pagination, usePaginated } from '@/components/Pagination';
import { useSortable, SortHeader } from '@/components/SortableTable';
import { PracaFormModal } from '@/components/PracaFormModal';
import { VOIVODESHIPS } from '@/data/poland-geo';
import { formatPlnWhole } from '@/lib/money';

type SortKey = 'seqNo' | 'client' | 'title' | 'kind' | 'status' | 'deadline' | 'executed' | 'amount';

/** „10–12.09.2026" dla zakresu, „20.09.2026" dla jednego dnia. */
function fmtRange(from: string | null, to: string | null): string {
  if (!from) return '—';
  const d = (iso: string) => iso.split('-').reverse().join('.');
  if (!to || to === from) return d(from);
  return `${d(from)} – ${d(to)}`;
}

/**
 * Ewidencja prac terenowych (V154) — odpowiednik arkusza „ewidencja prac", tyle że filtrowalny.
 *
 * Filtry są jednym obiektem `Filter` wysyłanym do trzech endpointów naraz (lista, podsumowanie,
 * mapa). Dzięki temu liczniki nad tabelą zawsze opisują to, co widać w tabeli — a nie cały
 * rejestr — i mapa pokazuje ten sam wycinek.
 */
export function PracePage() {
  const nav = useNavigate();
  // Mapa prac i karta klienta linkują tu z ?clientId= — filtr musi startować z adresu,
  // inaczej „Prace klienta →" otwierałoby pełny rejestr i użytkownik szukałby ręcznie.
  const [params] = useSearchParams();
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'FIELDWORK_EDIT');
  const canSeePricing = hasPermission(user, 'PRICING_VIEW');
  const canSeeFinance = hasPermission(user, 'FINANCE_VIEW');

  const [rows, setRows] = useState<FieldWork[] | null>(null);
  const [summary, setSummary] = useState<FieldWorkSummary | null>(null);
  const [meta, setMeta] = useState<FieldWorkMeta | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statuses, setStatuses] = useState<FieldWorkStatusCode[]>([]);
  const [kind, setKind] = useState<FieldWorkKindCode | ''>('');
  const [clientId, setClientId] = useState<string>(params.get('clientId') ?? '');
  // Link „Pokaż w rejestrze prac →" z karty zlecenia. Bez tego prowadził na pełną listę,
  // co użytkownik odczytywał jako „zlecenie obejmuje cały rejestr".
  const [orderId, setOrderId] = useState<string>(params.get('orderId') ?? '');
  const [voivodeship, setVoivodeship] = useState('');
  const [settled, setSettled] = useState<'' | 'true' | 'false'>('');
  const [overdue, setOverdue] = useState(false);
  const [executedFrom, setExecutedFrom] = useState('');
  const [executedTo, setExecutedTo] = useState('');
  const [deadlineFrom, setDeadlineFrom] = useState('');
  const [deadlineTo, setDeadlineTo] = useState('');
  const [amountFrom, setAmountFrom] = useState('');
  const [amountTo, setAmountTo] = useState('');
  const [moreFilters, setMoreFilters] = useState(!!params.get('clientId'));
  /** Numer zlecenia do pigułki nad tabelą — bierzemy z pierwszego wiersza, bo filtr jest po id. */
  const orderLabel = orderId ? (rows?.find(r => r.orderNumber)?.orderNumber ?? `#${orderId}`) : null;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FieldWork | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(h);
  }, [search]);

  const filter: FieldWorkFilter = useMemo(() => ({
    search: debounced || undefined,
    status: statuses.length ? statuses : undefined,
    kind: kind ? [kind] : undefined,
    clientId: clientId ? Number(clientId) : undefined,
    orderId: orderId ? Number(orderId) : undefined,
    voivodeship: voivodeship || undefined,
    settled: settled === '' ? undefined : settled === 'true',
    overdue: overdue ? true : undefined,
    executedFrom: executedFrom || undefined,
    executedTo: executedTo || undefined,
    deadlineFrom: deadlineFrom || undefined,
    deadlineTo: deadlineTo || undefined,
    amountFrom: amountFrom ? Number(amountFrom) : undefined,
    amountTo: amountTo ? Number(amountTo) : undefined,
  }), [debounced, statuses, kind, clientId, orderId, voivodeship, settled, overdue,
       executedFrom, executedTo, deadlineFrom, deadlineTo, amountFrom, amountTo]);

  const reload = () => {
    setErr(null);
    api.list(filter).then(setRows).catch(e => setErr(e?.message ?? String(e)));
    api.summary(filter).then(setSummary).catch(() => {});
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);
  useEffect(() => {
    api.meta().then(setMeta).catch(() => {});
    clientsApi.list().then(setClients).catch(() => {});
  }, []);

  const toggleStatus = (s: FieldWorkStatusCode) =>
    setStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const clearFilters = () => {
    setSearch(''); setStatuses([]); setKind(''); setClientId(''); setOrderId(''); setVoivodeship('');
    setSettled(''); setOverdue(false);
    setExecutedFrom(''); setExecutedTo(''); setDeadlineFrom(''); setDeadlineTo('');
    setAmountFrom(''); setAmountTo('');
  };

  const activeFilters = [
    debounced, statuses.length ? 'x' : '', kind, clientId, orderId, voivodeship, settled,
    overdue ? 'x' : '', executedFrom, executedTo, deadlineFrom, deadlineTo, amountFrom, amountTo,
  ].filter(Boolean).length;

  const { sorted, sort, onHeaderClick } = useSortable<FieldWork, SortKey>(
    rows ?? [], 'seqNo', 'desc',
    (r, key) => {
      switch (key) {
        case 'seqNo': return r.seqNo;
        case 'client': return r.clientName;
        case 'title': return r.title;
        case 'kind': return r.kindName;
        case 'status': return r.statusName;
        case 'deadline': return r.contractDeadline;
        case 'executed': return r.firstDate;
        case 'amount': return r.netAmount;
        default: return null;
      }
    });

  const { pagedItems, page, pageSize, setPage, setPageSize, total } =
    usePaginated(sorted, { storageKey: 'field-works' });

  const quickStatus = (w: FieldWork, next: FieldWorkStatusCode) => {
    api.changeStatus(w.id, next).then(reload).catch(e => setErr(e?.message ?? String(e)));
  };

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1>Prace terenowe</h1>
          <div className="text-ink-400 text-[13px]">
            Ewidencja robót na studniach — regeneracje, inspekcje TV, remonty
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => nav('/mapa-przeglad')} className="btn btn-secondary btn-sm">
            <Icon name="map" className="w-3.5 h-3.5"/> Mapa
          </button>
          {canEdit && (
            <button onClick={() => { setEditing(null); setModalOpen(true); }} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Nowa praca
            </button>
          )}
        </div>
      </header>

      {orderId && (
        <div className="flex items-center gap-2 mb-3 text-[13px]">
          <span className="pill pill-water">Zlecenie: {orderLabel}</span>
          <button onClick={() => setOrderId('')} className="text-ink-500 hover:text-ink-800 text-[12px]">
            pokaż wszystkie prace
          </button>
        </div>
      )}

      {/* KPI liczone nad AKTUALNIE przefiltrowanym zestawem — nie nad całym rejestrem. */}
      {summary && (
        <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: `repeat(${canSeePricing ? 6 : 5}, minmax(0,1fr))` }}>
          <Kpi label="Prace" value={summary.total}/>
          <Kpi label="Zlecone" value={summary.ordered} tone="water"/>
          <Kpi label="W toku" value={summary.inProgress} tone="amber"/>
          <Kpi label="Po terminie" value={summary.overdue} tone={summary.overdue > 0 ? 'red' : undefined}
               hint={summary.dueIn7Days > 0 ? `${summary.dueIn7Days} w ciągu 7 dni` : undefined}/>
          <Kpi label="Nierozliczone" value={summary.unsettled}
               hint={canSeePricing && summary.netUnsettled != null ? formatPlnWhole(summary.netUnsettled) : undefined}/>
          {canSeePricing && <Kpi label="Suma netto" value={formatPlnWhole(summary.netTotal)}/>}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-head flex-wrap gap-2">
          <div className="flex items-center gap-2 bg-ink-50 border border-ink-100 px-2.5 py-1.5 rounded-md text-ink-400 flex-1 min-w-[240px] max-w-[420px]">
            <Icon name="search"/>
            <input value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Szukaj: opis, studnia, miejscowość, klient, nr…"
                   className="flex-1 border-0 bg-transparent outline-none text-[13px] text-ink-800"/>
          </div>

          {/* Statusy jako przełączniki, nie select: „zlecone + w toku" to najczęstszy widok
              rejestru i nie może wymagać trzech kliknięć w rozwijanej liście. */}
          <div className="flex gap-1">
            {(meta?.statuses ?? []).map(s => (
              <button key={s.id} onClick={() => toggleStatus(s.id)}
                      className={'btn btn-xs ' + (statuses.includes(s.id) ? 'btn-primary' : 'btn-ghost')}>
                <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: s.mapColor }}/>
                {s.name}
              </button>
            ))}
          </div>

          <div className="w-[190px]">
            <SearchableSelect value={kind} onChange={v => setKind(v as FieldWorkKindCode | '')}>
              <option value="">Rodzaj — wszystkie</option>
              {(meta?.kinds ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </SearchableSelect>
          </div>

          <button onClick={() => setMoreFilters(v => !v)}
                  className={'btn btn-xs ' + (moreFilters || activeFilters > 0 ? 'btn-secondary' : 'btn-ghost')}>
            <Icon name="filter" className="w-3.5 h-3.5"/> Więcej filtrów
            {activeFilters > 0 && <span className="ml-1 pill pill-water text-[10px]">{activeFilters}</span>}
          </button>
          {activeFilters > 0 && (
            <button onClick={clearFilters} className="btn btn-ghost btn-xs text-ink-500">Wyczyść</button>
          )}
        </div>

        {moreFilters && (
          <div className="px-4 py-3 border-t border-ink-100 grid grid-cols-4 gap-3 bg-ink-50/40">
            <label className="field">
              <span className="field-label">Klient</span>
              <SearchableSelect value={clientId} onChange={setClientId} clearable>
                <option value="">— wszyscy —</option>
                {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Województwo</span>
              <SearchableSelect value={voivodeship} onChange={setVoivodeship} clearable>
                <option value="">— wszystkie —</option>
                {VOIVODESHIPS.map(v => <option key={v} value={v}>{v}</option>)}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Rozliczenie</span>
              <SearchableSelect value={settled} onChange={v => setSettled(v as '' | 'true' | 'false')}>
                <option value="">— wszystkie —</option>
                <option value="false">Nierozliczone</option>
                <option value="true">Rozliczone</option>
              </SearchableSelect>
            </label>
            <label className="flex items-end gap-2 pb-1.5 text-[13px] cursor-pointer">
              <input type="checkbox" checked={overdue} onChange={e => setOverdue(e.target.checked)}/>
              <span>Tylko po terminie umowy</span>
            </label>

            <label className="field">
              <span className="field-label">Wykonane od</span>
              <input type="date" value={executedFrom} onChange={e => setExecutedFrom(e.target.value)} className="input"/>
            </label>
            <label className="field">
              <span className="field-label">Wykonane do</span>
              <input type="date" value={executedTo} onChange={e => setExecutedTo(e.target.value)} className="input"/>
            </label>
            <label className="field">
              <span className="field-label">Termin umowy od</span>
              <input type="date" value={deadlineFrom} onChange={e => setDeadlineFrom(e.target.value)} className="input"/>
            </label>
            <label className="field">
              <span className="field-label">Termin umowy do</span>
              <input type="date" value={deadlineTo} onChange={e => setDeadlineTo(e.target.value)} className="input"/>
            </label>

            {canSeePricing && (
              <>
                <label className="field">
                  <span className="field-label">Kwota netto od</span>
                  <input type="number" value={amountFrom} onChange={e => setAmountFrom(e.target.value)}
                         className="input font-mono" placeholder="0"/>
                </label>
                <label className="field">
                  <span className="field-label">Kwota netto do</span>
                  <input type="number" value={amountTo} onChange={e => setAmountTo(e.target.value)}
                         className="input font-mono" placeholder="999999"/>
                </label>
              </>
            )}
            <div className="col-span-2 flex items-end text-[11px] text-ink-500 pb-2">
              Filtr „wykonane" działa po terminach wykonania. Pozycje bez rozpoznanej daty
              (import ze słownego zapisu) nie wpadną w to okno.
            </div>
          </div>
        )}

        {/* overflow-x-auto, nie samo !p-0: rejestr ma 10 kolumn i przy 1600 px szerokość tabeli
            (~1670 px) przekracza kontener. Bez tego ostatnie kolumny — kwota, faktura i przycisk
            edycji — są przycięte i NIE DA SIĘ do nich doscrollować, bo strona nie scrolluje w bok. */}
        <div className="card-body !p-0 overflow-x-auto">
          {err && <div className="text-red-600 p-4">Błąd: {err}</div>}
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="text-ink-400 p-6 text-center">
              {activeFilters > 0 ? 'Brak prac pasujących do filtrów.' : 'Rejestr jest pusty. Dodaj pierwszą pracę.'}
            </div>
          )}
          {rows && rows.length > 0 && (
            <>
              <table className="table-hw">
                <thead>
                  <tr>
                    <SortHeader label="Lp." sortKey="seqNo" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Klient" sortKey="client" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Opis prac" sortKey="title" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Rodzaj" sortKey="kind" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Status" sortKey="status" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Terminy wykonania" sortKey="executed" current={sort} onClick={onHeaderClick}/>
                    <SortHeader label="Termin umowy" sortKey="deadline" current={sort} onClick={onHeaderClick}/>
                    {canSeePricing && <SortHeader label="Netto" sortKey="amount" current={sort} onClick={onHeaderClick} align="right"/>}
                    {canSeeFinance && <th>Faktura</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map(w => (
                    <tr key={w.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => nav(`/prace/${w.id}`)}>
                      <td className="font-mono text-[12px] text-ink-500">{w.seqNo}</td>
                      <td className="text-[13px]">
                        <div className="text-ink-900 font-medium truncate max-w-[220px]">{w.clientName ?? '—'}</div>
                        {w.clientCity && <div className="text-[11px] text-ink-400">{w.clientCity}</div>}
                      </td>
                      <td className="text-[13px] text-ink-800">
                        <div className="truncate max-w-[380px]">{w.title}</div>
                        <div className="text-[11px] text-ink-400 truncate max-w-[380px]">
                          {[w.wellLabel && `studnia ${w.wellLabel}`, w.locationText, w.parcel]
                            .filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td><span className="pill pill-earth whitespace-nowrap">{w.kindName}</span></td>
                      <td onClick={e => e.stopPropagation()}>
                        {canEdit ? (
                          <SearchableSelect value={w.status}
                                            onChange={v => quickStatus(w, v as FieldWorkStatusCode)}>
                            {(meta?.statuses ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </SearchableSelect>
                        ) : (
                          <span className={'pill pill-' + w.statusColor}>{w.statusName}</span>
                        )}
                      </td>
                      <td className="text-[12px] text-ink-700 whitespace-nowrap">
                        {w.dates.length === 0 ? <span className="text-ink-300">—</span> : (
                          <>
                            {fmtRange(w.firstDate, w.lastDate)}
                            <div className="text-[11px] text-ink-400">
                              {w.dates.length} {w.dates.length === 1 ? 'wpis' : 'wpisy'} · {w.totalDays} dni
                            </div>
                          </>
                        )}
                      </td>
                      <td className="text-[12px] whitespace-nowrap">
                        {w.contractDeadline ? (
                          <>
                            <span className="font-mono text-ink-700">{w.contractDeadline}</span>
                            {w.daysToDeadline != null && (
                              <div className={'text-[11px] ' + (w.daysToDeadline < 0 ? 'text-red-600 font-semibold'
                                : w.daysToDeadline <= 7 ? 'text-amber-600' : 'text-ink-400')}>
                                {w.daysToDeadline < 0
                                  ? `po terminie ${-w.daysToDeadline} dni`
                                  : `za ${w.daysToDeadline} dni`}
                              </div>
                            )}
                          </>
                        ) : w.deadlineNote
                          ? <span className="text-[11px] text-ink-500 italic">{w.deadlineNote}</span>
                          : <span className="text-ink-300">—</span>}
                      </td>
                      {canSeePricing && (
                        <td className="font-mono text-[12px] text-ink-800 text-right whitespace-nowrap">
                          {formatPlnWhole(w.netAmount)}
                        </td>
                      )}
                      {canSeeFinance && (
                        <td className="text-[12px] whitespace-nowrap">
                          {w.invoiceCount > 0
                            ? <span className="pill pill-green">{w.invoiceCount} fv</span>
                            : w.invoicedDate
                              // Import historyczny: dokumentu w systemie nie ma, ale data faktury jest.
                              ? <span className="pill pill-earth" title="Data faktury z importu — brak dokumentu w systemie">
                                  {w.invoicedDate}
                                </span>
                              : <span className="text-ink-300">—</span>}
                        </td>
                      )}
                      <td className="text-right" onClick={e => e.stopPropagation()}>
                        {canEdit && (
                          <button onClick={() => { setEditing(w); setModalOpen(true); }}
                                  className="btn btn-secondary btn-sm" title="Edytuj">
                            <Icon name="edit" className="w-3.5 h-3.5"/>
                          </button>
                        )}
                        <Icon name="chevron" className="text-ink-300 inline ml-2"/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 border-t border-ink-100">
                <Pagination total={total} page={page} pageSize={pageSize}
                            onPageChange={setPage} onPageSizeChange={setPageSize}/>
              </div>
            </>
          )}
        </div>
      </div>

      <PracaFormModal
        open={modalOpen}
        initial={editing}
        clients={clients}
        meta={meta}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); reload(); }}
      />
    </div>
  );
}

function Kpi({ label, value, hint, tone }: {
  label: string; value: number | string; hint?: string; tone?: 'water' | 'amber' | 'red';
}) {
  const color = tone === 'red' ? 'text-red-600' : tone === 'amber' ? 'text-amber-600'
    : tone === 'water' ? 'text-water-600' : 'text-ink-800';
  return (
    <div className="card">
      <div className="card-body !py-3">
        <div className={'text-[24px] font-bold leading-none ' + color}>{value}</div>
        <div className="text-[11px] text-ink-500 uppercase tracking-wider mt-1">{label}</div>
        {hint && <div className="text-[11px] text-ink-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
